import type { VideoProbe } from './probe';

/**
 * What a creative must be to survive the kiosk (docs/CREATIVE_SPEC.md).
 *
 * These are playback constraints, not taste: the station player is a Chromium
 * kiosk, so anything it cannot decode is a black screen the advertiser paid for.
 */
export const CREATIVE_SPEC = {
  /**
   * What ends up in storage. Anything ffprobe opens is accepted at upload —
   * `.mov` from AirDrop/Telegram, `.mkv`, `.webm` — and remuxed to MP4 with
   * `moov` first on the server (`lib/video/codec#normalizeVideo`). Kiosk and
   * auto-moderation only ever see MP4.
   */
  container: 'mp4',
  /** Chromium on the kiosk decodes H.264 reliably; HEVC/AV1/VP9 it does not. */
  codecs: ['h264'],
  /** 8-bit 4:2:0 only — 10-bit and 4:2:2/4:4:4 fail on the same decoder. */
  pixelFormats: ['yuv420p', 'yuvj420p'],
  /** Shortest side, in pixels. 1080p is the station panel's own resolution. */
  minSide: 1080,
  /**
   * Slot grid (docs/CAMPAIGNS.md): inventory is sold in 5-second slots and a
   * clip is billed for the whole slots it occupies (a 41s clip takes a 45s slot,
   * a 17s clip a 20s slot — see `slotVideoLength`), so 5–120s is the sellable
   * range. Anything under a slot can't be booked; anything over 120s is refused.
   */
  minDurationSec: 4.5,
  maxDurationSec: 120,
} as const;

export type CreativeRejection =
  | 'codec'
  | 'pixel_format'
  | 'resolution'
  | 'duration'
  | 'no_video_stream';

export interface CreativeVerdict {
  ok: boolean;
  reason?: CreativeRejection;
  /** Ready to show to the advertiser — they are the ones who must fix the file. */
  message?: string;
}

/**
 * Does the stored object already sit in an MP4 container?
 *
 * ffprobe names MP4 and MOV with one and the same string
 * (`mov,mp4,m4a,3gp,3g2,mj2`), so `format_name` alone can't tell a QuickTime
 * file from an MP4. The rule is therefore conservative: MP4 only when the
 * family contains `mp4` **and** the key ends in `.mp4`/`.m4v`. Everything
 * else — `.mov`, `matroska,webm`, `avi`, `mpegts`, a key without an
 * extension — goes to the lossless remux. A real MP4 under a foreign
 * extension costs us a few seconds of `-c copy`; a MOV let through costs an
 * advertiser a black slot and a Rekognition failure, so the false positive is
 * the side to err on. Pure.
 */
export function isMp4Container(formatName: string, fileName?: string): boolean {
  const family = (formatName || '')
    .toLowerCase()
    .split(',')
    .map((s) => s.trim());
  if (!family.includes('mp4')) return false;
  if (!fileName) return false;
  const bare = fileName.split('?')[0].split('#')[0];
  return /\.(mp4|m4v)$/i.test(bare);
}

/**
 * Validate a probed file against {@link CREATIVE_SPEC}. Pure.
 *
 * Контейнер здесь не проверяется: это не отказ, а переупаковка на сервере —
 * см. `isMp4Container` и приёмку в `upload/complete`.
 */
export function validateCreative(probe: VideoProbe): CreativeVerdict {
  if (!probe.width || !probe.height) {
    return {
      ok: false,
      reason: 'no_video_stream',
      message: 'Файл не содержит видеодорожки или повреждён.',
    };
  }

  // Разрешение и хронометраж идут первыми: их не починить перекодированием,
  // а приёмка решает «в очередь или отказ» по первой же причине. Если бы кодек
  // проверялся раньше, 720p-ролик в HEVC ушёл бы в очередь и провалился там
  // молча, вместо честного 400 с объяснением.
  if (Math.min(probe.width, probe.height) < CREATIVE_SPEC.minSide) {
    return {
      ok: false,
      reason: 'resolution',
      message:
        `Разрешение ${probe.width}×${probe.height} ниже требуемого. ` +
        `Минимум — ${CREATIVE_SPEC.minSide}p по короткой стороне (Full HD).`,
    };
  }

  if (
    probe.durationSec < CREATIVE_SPEC.minDurationSec ||
    probe.durationSec > CREATIVE_SPEC.maxDurationSec
  ) {
    return {
      ok: false,
      reason: 'duration',
      message:
        `Хронометраж ${probe.durationSec.toFixed(1)} с вне допустимого диапазона. ` +
        'Ролик должен длиться от 5 до 120 секунд.',
    };
  }

  if (!CREATIVE_SPEC.codecs.includes(probe.codec as (typeof CREATIVE_SPEC.codecs)[number])) {
    return {
      ok: false,
      reason: 'codec',
      message:
        `Кодек ${probe.codec.toUpperCase() || 'неизвестен'} не воспроизводится на станциях. ` +
        'Пересохраните ролик в MP4 / H.264 (в экспорте iPhone выберите «Наиболее совместимый»).',
    };
  }

  if (!CREATIVE_SPEC.pixelFormats.includes(probe.pixFmt as (typeof CREATIVE_SPEC.pixelFormats)[number])) {
    return {
      ok: false,
      reason: 'pixel_format',
      message:
        `Цветовая схема ${probe.pixFmt || 'неизвестна'} не воспроизводится на станциях. ` +
        'Нужен 8-битный H.264 (yuv420p).',
    };
  }

  return { ok: true };
}

export type IngestDecision =
  /** Spec-conforming MP4: store as is (the route still checks `moov` placement). */
  | { action: 'accept' }
  /** Playable after a server-side fix: queue for `normalizeVideo`, status `processing`. */
  | { action: 'normalize'; reason: 'codec' | 'pixel_format' | 'container' }
  /** Nothing we can do for the advertiser: 400 with the verdict's message. */
  | { action: 'reject'; reason: CreativeRejection; message: string };

/**
 * What upload/complete does with a probed object. Pure.
 *
 * Граница проходит по тому, что мы умеем починить за клиента. Кодек, цветовая
 * схема и контейнер — вопрос настроек экспорта (iPhone пишет HEVC, AirDrop
 * отдаёт `.mov`): перекодируем или переупаковываем сами. Разрешение и
 * хронометраж переделать нельзя — честный отказ. Порядок внутри
 * `validateCreative` гарантирует, что отказ побеждает очередь: 720p в любом
 * контейнере и кодеке — это 400, а не задача, которая провалится молча.
 */
export function decideIngest(probe: VideoProbe, storageKey?: string): IngestDecision {
  const verdict = validateCreative(probe);
  if (!verdict.ok) {
    const reason = verdict.reason as CreativeRejection;
    if (reason === 'codec' || reason === 'pixel_format') return { action: 'normalize', reason };
    return { action: 'reject', reason, message: verdict.message as string };
  }
  if (!isMp4Container(probe.formatName, storageKey)) {
    return { action: 'normalize', reason: 'container' };
  }
  return { action: 'accept' };
}

/**
 * Aspect-ratio bucket for a probed file. Kept in sync with the `Video.aspectRatio`
 * enum; the browser-side classifier in `lib/utils/video-metadata` follows the
 * same thresholds so client and server never disagree about a clip.
 */
export function classifyAspect(width: number, height: number): '16:9' | '9:16' | '4:3' | '1:1' {
  const ratio = height > 0 ? width / height : 1;
  const candidates: Array<{ label: '16:9' | '9:16' | '4:3' | '1:1'; value: number }> = [
    { label: '16:9', value: 16 / 9 },
    { label: '9:16', value: 9 / 16 },
    { label: '4:3', value: 4 / 3 },
    { label: '1:1', value: 1 },
  ];
  return candidates.reduce((best, c) =>
    Math.abs(ratio - c.value) < Math.abs(ratio - best.value) ? c : best,
  ).label;
}
